import { Fragment, useContext, useEffect, useState } from "react";
import { Row, Col, Button, Card, Collapse, OverlayTrigger, Tooltip, Alert, Form } from "react-bootstrap";
import { Address } from "../Physician/Models/Address";
import { abpnClient } from "../../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import { CountryLookupItem, StateWithCountryLookupItem } from "../Shared/Models/StateWithCountryLookupItem";
import AddressForm from "../Shared/Components/AddressForm";
import { AddressType } from "../Shared/Enums/AddressType";
import { CountryId } from "../Shared/Enums/CountryId";
import { deepCopy } from "../Shared/Utility/DeepCopy";
import { Prompt } from "react-router-dom";

interface IProps {
    isSectionOpen: boolean;
    physicianId: number;
    addresses: Array<Address>;
    states: Array<StateWithCountryLookupItem>;
    countries: Array<CountryLookupItem>;
}

export default function AddressesCard(props: IProps) {
    const [addresses, setAddresses] = useState<Array<Address>>(props.addresses);
    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);
    const [contactAddress, setContactAddress] = useState<Address>(null);
    const [shippingAddress, setShippingAddress] = useState<Address>(null);
    const [publicAddress, setPublicAddress] = useState<Address>(null);
    const [isEditingContactAddress, setIsEditingContactAddress] = useState<boolean>(false);
    const [isEditingShippingAddress, setIsEditingShippingAddress] = useState<boolean>(false);
    const [isEditingPublicAddress, setIsEditingPublicAddress] = useState<boolean>(false);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [hasEUCode, setHasEUCode] = useState<boolean>(false);

    useEffect(() => {
        setAddressesFromList(addresses);
        checkForEuCode();
    }, [addresses]);

    const authContext = useContext(AuthContext);

    const saveAddress = (address: Address) => {
        setIsEditingContactAddress(false);
        setIsEditingShippingAddress(false);
        setIsEditingPublicAddress(false);
        let url = "/api/Physician/SaveAddresses/";
        let addressList = buildAddressList(address);
        abpnClient.post<Array<Address>>(url, addressList, authContext).then(result => {
            setAddresses(result);
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
            }, 5000)
        });
    }

    const saveOptInOut = (opt: boolean) => {
        let url = "/api/Physician/OptInOut/";
        abpnClient.post(url, opt, authContext);
    }

    const setAddressesFromList = (addresses: Array<Address>) => {
        let contactAddress = addresses.find(_ => _.isContact);
        let shippingAddress = addresses.find(_ => !_.isContact && _.types.includes(AddressType.Shipping));
        let publicAddress = addresses.find(_ =>  !_.isContact && _.types.includes(AddressType.Public));
        setContactAddress(contactAddress ?? createNewAddress(AddressType.Contact));
        setShippingAddress(shippingAddress ?? createNewAddress(AddressType.Shipping));
        setPublicAddress(publicAddress ?? createNewAddress(AddressType.Public));
    }

    const checkForEuCode = () => {
        let euCodes = [103, 105, 112, 113, 120, 125, 126, 129, 131, 137, 139, 142, 147, 154, 157, 165, 179, 170, 171, 183, 185];
        let publicAddress = addresses.find(_ => _.types.includes(AddressType.Public));
        setHasEUCode(euCodes.includes(publicAddress.countryId));
    }

    const createNewAddress = (type: AddressType) => {
        let newAddress = new Address();
        newAddress.countryId = CountryId.Usa;
        newAddress.types.push(type);
        return newAddress;
    }

    const buildAddressList = (address: Address) => {
        let addressList = new Array<Address>();

        if (address.isContact) {
            addressList = deepCopy(addresses);
            addressList.splice(addressList.findIndex(_ => _.isContact), 1);
        } else {
            if (address.types.length == 0) {
                //Set new address to use for all 3 types
                address.types.push(AddressType.Contact);
                address.types.push(AddressType.Public);
                address.types.push(AddressType.Shipping);
                addressList.push(address);
                return addressList;
            } else if (address.types.includes(AddressType.Shipping) && address.types.includes(AddressType.Public)) {
                let updatedContact = deepCopy(addresses.find(_ => _.isContact))
                updatedContact = removeAddressType(updatedContact, AddressType.Shipping);
                updatedContact = removeAddressType(updatedContact, AddressType.Public);
                addressList.push(updatedContact);
            } else if (address.types.includes(AddressType.Shipping)) {
                let updatedContact = deepCopy(addresses.find(_ => _.isContact))
                if (updatedContact) {
                    addressList.push(removeAddressType(updatedContact, AddressType.Shipping));
                }

                let updatedPublic = deepCopy(addresses.find(_ => !_.isContact && _.types.includes(AddressType.Public)))
                if (updatedPublic) {
                    addressList.push(removeAddressType(updatedPublic, AddressType.Shipping));
                }
            } else if (address.types.includes(AddressType.Public)) {
                let updatedContact = deepCopy(addresses.find(_ => _.isContact))
                if (updatedContact) {
                    addressList.push(removeAddressType(updatedContact, AddressType.Public));
                }

                let updatedShipping = deepCopy(addresses.find(_ => !_.isContact && _.types.includes(AddressType.Shipping)))
                if (updatedShipping) {
                    addressList.push(removeAddressType(updatedShipping, AddressType.Public));
                }
            }
        }

        addressList.push(address);
        return addressList;
    }

    const removeAddressType = (address: Address, type: AddressType) => {
        if (address.types.includes(type)) {
            address.types.splice(address.types.indexOf(type), 1);
        }
        return address;
    }

    const displayAddress = (address: Address, isPublic: boolean = false) => {
        if(!address?.stateId) return <Card.Text className="pp-font-size-smaller">No Address Provided</Card.Text>

        return <Card.Text className="pp-font-size-smaller">
            {!isPublic &&
                <Fragment>
                    <div>{address?.line1}</div>
                    <div>{address?.line2}</div>
                </Fragment>}
                    <div>{address?.city}, {props.states.find(_ => _.id === address?.stateId)?.shortName} {address?.zipCode}</div>
                    <div>{props.countries.find(_ => _.id === address?.countryId)?.name}</div>
            {!isPublic &&
                <Fragment>
                    <br />
                    <div>{address?.homePhoneNumber}</div>
                </Fragment>}
        </Card.Text>
    }

    const getGDPRText = () => {
        let pubAddress = addresses.find(_ => _.types.includes(AddressType.Public));
        let hasntOptedIn = pubAddress.dateOptedIn === null || pubAddress.dateOptedIn === undefined;
        return <Form.Group controlId="formAttestationControls">
            <div className="mb-1 ml-3 mr-3">Because you have an EU public address, the GDPR requires the ABPN to suppress your diplomate information in ABPN's public-facing board certification verification system, verifyCERT.  To opt-in to remove the suppression of your diplomate information in verifyCERT, please check the Opt-in box below.</div>
            <div className="mb-1 ml-3 mr-3">I hereby consent and do authorize the ABPN to process and publish my certification details, including city and country of current residence and waive and any applicable privacy rules or regulations relating to my certification data and status.</div>
            <Form.Check className="ml-3" type="checkbox">
                <Form.Check.Input defaultChecked={!hasntOptedIn} type="checkbox" required onChange={(e: React.ChangeEvent<HTMLInputElement>) =>  saveOptInOut(e.target.checked)} />
                <Form.Check.Label>Opt-in</Form.Check.Label>
            </Form.Check>
        </Form.Group>
    }

    if (!props.addresses || contactAddress == null)
        return (
            <Card border="pp-secondary-grey">
                <Prompt
                    when={isEditingContactAddress || isEditingShippingAddress || isEditingPublicAddress}
                    message='You have unsaved changes, are you sure you want to leave?'
                />
                {showSuccess && <Alert variant="pp-alert-success">Success! Address has been updated.</Alert>}
                <Card.Body>
                    <Card.Title className="font-weight-bolder" onClick={() => setOpenCollapse(!openCollapse)}>
                        <span className="text-pp-primary"><i className="font-weight-bolder las la-angle-down"></i> Addresses</span>
                    </Card.Title>
                    {hasEUCode && <Row>{getGDPRText()}</Row>}
                    <Collapse in={openCollapse}>
                        <Row>
                            <Col xs={12} lg={4} className="px-2">
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title className="font-weight-bold">Contact Address
                                            <OverlayTrigger
                                                placement={"right"}
                                                overlay={<Tooltip id="contactAddress" bsPrefix="pp-tooltip">
                                                    ABPN highly recommends keeping a personal email address on file to ensure you receive all communications regarding applications, exams, certification requirements, pertinent updates, and to access your ABPN Physician Portal account.
                                                    This will be used to log into the Physician Portal.
                                                </Tooltip>}>
                                                <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                            </OverlayTrigger>
                                        </Card.Title>
                                        {isEditingContactAddress ?
                                            <AddressForm
                                                address={contactAddress}
                                                states={props.states}
                                                countries={props.countries}
                                                runValidation={false}
                                                onSubmit={saveAddress}
                                                onCancel={() => setIsEditingContactAddress(false)}
                                                submitText={'Save'}
                                                showCancel={true} />
                                            :
                                            <Fragment>
                                                <br />
                                                <br />
                                                <br />
                                                <Button
                                                    disabled={isEditingPublicAddress || isEditingShippingAddress}
                                                    className="font-weight-bold"
                                                    variant="pp-primary"
                                                    onClick={() => setIsEditingContactAddress(true)}>
                                                    Add
                                                </Button>
                                            </Fragment>
                                        }
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={12} lg={4} className="px-2">
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title className="font-weight-bold">Certificate Shipping
                                            <OverlayTrigger
                                                placement={"right"}
                                                overlay={<Tooltip id="contactAddress" bsPrefix="pp-tooltip">
                                                    This address will be used for shipment of your certificate and will expire from our system six months after the exam window end date.
                                                </Tooltip>}>
                                                <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                            </OverlayTrigger>
                                        </Card.Title>
                                        <Fragment>
                                            <br />
                                            <br />
                                            <br />
                                        </Fragment>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={12} lg={4} className="px-2">
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title className="font-weight-bold">Public
                                            <OverlayTrigger
                                                placement={"right"}
                                                overlay={<Tooltip id="contactAddress" bsPrefix="pp-tooltip">
                                                    The Board will submit your name and public address for inclusion in the ABMS’s Certification Matters website, certificationmatters.org upon certification. Lists of diplomates are also sent to other select publications and your name and publication address will be included in these lists.
                                                    <br />
                                                    Due to the European General Data Protection Regulation (GDPR), diplomates with a current UK/EU address on file will no longer be published on our VerifyCert directory, or reported to credentialers or any other third parties.  Additional GDPR information:  https://gdpr-info.eu/
                                                    <br />
                                                    <br />
                                                    Note: diplomates with a UK/EU addresses do not have their certification status published due to GDPR regulations.
                                                </Tooltip>}>
                                                <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                            </OverlayTrigger>
                                        </Card.Title>
                                        <Fragment>
                                            <br />
                                            <br />
                                            <br />
                                        </Fragment>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Collapse>
                </Card.Body>
            </Card>
        )
    return (
        <Card border="pp-secondary-grey">
            <Prompt
                when={isEditingContactAddress || isEditingShippingAddress || isEditingPublicAddress}
                message='You have unsaved changes, are you sure you want to leave?'
            />
            {showSuccess && <Alert variant="pp-alert-success">Success! Address has been updated.</Alert>}
            <Card.Body>
                <Card.Title className="font-weight-bolder" onClick={() => setOpenCollapse(!openCollapse)}>
                    <span className="text-pp-primary"><i className="font-weight-bolder las la-angle-down"></i> Addresses</span>
                </Card.Title>
                {hasEUCode && <Row>{getGDPRText()}</Row>}
                <Collapse in={openCollapse}>
                    <Row>
                        <Col xs={12} lg={4} className="px-2">
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title className="font-weight-bold">Contact Address
                                        <OverlayTrigger
                                            placement={"right"}
                                            overlay={<Tooltip id="contactAddress" bsPrefix="pp-tooltip">
                                                ABPN highly recommends keeping a personal email address on file to ensure you receive all communications regarding applications, exams, certification requirements, pertinent updates, and to access your ABPN Physician Portal account.
                                                This will be used to log into the Physician Portal.
                                            </Tooltip>}>
                                            <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                        </OverlayTrigger>
                                    </Card.Title>
                                    {isEditingContactAddress ?
                                        <AddressForm
                                            address={contactAddress}
                                            states={props.states}
                                            countries={props.countries}
                                            runValidation={false}
                                            onSubmit={saveAddress}
                                            onCancel={() => setIsEditingContactAddress(false)}
                                            submitText={'Save'}
                                            showCancel={true} />
                                        :
                                        <Fragment>
                                            {displayAddress(contactAddress)}
                                            <Button
                                                disabled={isEditingPublicAddress || isEditingShippingAddress}
                                                className="font-weight-bold"
                                                variant="pp-primary"
                                                onClick={() => setIsEditingContactAddress(true)}>
                                                Edit
                                            </Button>
                                        </Fragment>
                                    }
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={12} lg={4} className="px-2">
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title className="font-weight-bold">Certificate Shipping
                                        <OverlayTrigger
                                            placement={"right"}
                                            overlay={<Tooltip id="contactAddress" bsPrefix="pp-tooltip">
                                                This address will be used for shipment of your certificate and will expire from our system six months after the exam window end date.
                                            </Tooltip>}>
                                            <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                        </OverlayTrigger>
                                    </Card.Title>
                                    {isEditingShippingAddress ?
                                        <AddressForm
                                            address={shippingAddress}
                                            states={props.states}
                                            countries={props.countries}
                                            runValidation={false}
                                            onSubmit={saveAddress}
                                            onCancel={() => setIsEditingShippingAddress(false)}
                                            submitText={'Save'}
                                            showCancel={true} />
                                        :
                                        <Fragment>
                                            {displayAddress(addresses.find(_ => !_.isContact && _.types.includes(AddressType.Shipping)) ? shippingAddress : contactAddress)}
                                            <Button
                                                disabled={isEditingPublicAddress || isEditingContactAddress}
                                                className="font-weight-bold"
                                                variant="pp-primary"
                                                onClick={() => setIsEditingShippingAddress(true)}>
                                                Edit
                                            </Button>
                                        </Fragment>
                                    }
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={12} lg={4} className="px-2">
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title className="font-weight-bold">Public
                                        <OverlayTrigger
                                            placement={"right"}
                                            overlay={<Tooltip id="contactAddress" bsPrefix="pp-tooltip">
                                                The Board will submit your name and public address for inclusion in the ABMS’s Certification Matters website, certificationmatters.org upon certification. Lists of diplomates are also sent to other select publications and your name and publication address will be included in these lists.
                                                <br />
                                                Due to the European General Data Protection Regulation (GDPR), diplomates with a current UK/EU address on file will no longer be published on our VerifyCert directory, or reported to credentialers or any other third parties.  Additional GDPR information:  https://gdpr-info.eu/
                                                <br />
                                                <br />
                                                Note: diplomates with a UK/EU addresses do not have their certification status published due to GDPR regulations.
                                            </Tooltip>}>
                                            <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                        </OverlayTrigger>
                                    </Card.Title>
                                    {isEditingPublicAddress ?
                                        <AddressForm
                                            address={publicAddress}
                                            states={props.states}
                                            countries={props.countries}
                                            runValidation={false}
                                            onSubmit={saveAddress}
                                            onCancel={() => setIsEditingPublicAddress(false)}
                                            submitText={'Save'}
                                            showCancel={true}
                                            showStreetAddress={false} />
                                        :
                                        <Fragment>
                                            {displayAddress(addresses.find(_ => !_.isContact && _.types.includes(AddressType.Public)) ? publicAddress : contactAddress, true)}
                                            <Button
                                                disabled={isEditingContactAddress || isEditingShippingAddress}
                                                className="font-weight-bold"
                                                variant="pp-primary"
                                                onClick={() => setIsEditingPublicAddress(true)}>
                                                Edit
                                            </Button>
                                        </Fragment>
                                    }
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Collapse>
            </Card.Body>
        </Card>
    );
}